import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, Check, Search } from 'lucide-react';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Combobox, type ComboboxOption } from '@/components/Combobox';
import { RoleSelect } from '@/components/RoleSelect';
import { Field } from '@/components/primitives';
import { useUpdateDashboard, useProfileSearch, useAddMember, useInviteMember, useUpdateMemberRole, useTenants, useTemplate, useCreateDashboard } from '@/api/hooks';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Edit dashboard metadata
// ---------------------------------------------------------------------------
export interface EditableDashboard {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number | null;
}

export function EditDashboardDialog({
  dashboard,
  tenantName,
  open,
  onClose,
}: {
  dashboard: EditableDashboard | null;
  tenantName?: string;
  open: boolean;
  onClose: () => void;
}) {
  const update = useUpdateDashboard();
  const [name, setName] = useState(dashboard?.name ?? '');
  const [description, setDescription] = useState(dashboard?.description ?? '');
  const [sortOrder, setSortOrder] = useState(String(dashboard?.sortOrder ?? 0));

  const handleSave = async () => {
    if (!dashboard) return;
    try {
      await update.mutateAsync({
        id: dashboard.id,
        name: name || dashboard.name,
        description,
        sortOrder: Number(sortOrder),
        _auditLabel: `"${dashboard.name}"${tenantName ? ` · ${tenantName}` : ''}`,
      });
      toast.success('Dashboard updated');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Dialog
      open={open}
      title={`Edit "${dashboard?.name ?? ''}"`}
      onClose={onClose}
      busy={update.isPending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={update.isPending || !name.trim()}>
            {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Sort order">
          <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Copy a dashboard to another tenant (same type), carrying its configuration
// ---------------------------------------------------------------------------
export function CopyDashboardDialog({
  sourceTenantId,
  typeOfTenantID,
  dashboard,
  open,
  onClose,
}: {
  sourceTenantId: string;
  typeOfTenantID: string;
  dashboard: Record<string, any> | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data: tenants } = useTenants();
  const { data: tpl, isLoading: tplLoading } = useTemplate(open ? (dashboard?.templateID ?? '') : '');
  const createDashboard = useCreateDashboard();
  const [search, setSearch] = useState('');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [carryParams, setCarryParams] = useState(true);

  const candidates = (tenants ?? [])
    .filter((t) => t.typeOfTenantID === typeOfTenantID && t.id !== sourceTenantId)
    .filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const reset = () => {
    setSearch('');
    setTargetId(null);
    setCarryParams(true);
  };

  const handleCopy = async () => {
    if (!dashboard || !targetId || !tpl) return;
    try {
      await createDashboard.mutateAsync({
        cluvioDashboardID: tpl.cluvioDashboard?.id || '',
        sharingToken: tpl.cluvioSharingToken?.token || '',
        name: dashboard.name,
        sortOrder: dashboard.sortOrder,
        description: dashboard.description,
        typeOfTenantID,
        tenantID: targetId,
        templateID: dashboard.templateID,
        fixedParameters: carryParams ? dashboard.fixedParameters : [],
        variableFilterOverrides: dashboard.variableFilterOverrides,
      });
      const target = candidates.find((t) => t.id === targetId);
      toast.success(`Dashboard copied to ${target?.name ?? 'tenant'}`);
      reset();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Copy failed');
    }
  };

  return (
    <Dialog
      open={open}
      title={`Copy "${dashboard?.name ?? ''}" to another tenant`}
      onClose={() => {
        if (!createDashboard.isPending) {
          reset();
          onClose();
        }
      }}
      busy={createDashboard.isPending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={createDashboard.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleCopy} disabled={createDashboard.isPending || !targetId || tplLoading || !tpl}>
            {createDashboard.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Copy dashboard
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${typeOfTenantID} tenants…`} className="pl-8 h-8 text-xs" />
        </div>
        <div className="max-h-48 overflow-auto divide-y divide-border/60 rounded-md border">
          {candidates.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">No other {typeOfTenantID} tenants</p>
          ) : (
            candidates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTargetId(t.id)}
                className={cn(
                  'flex w-full items-center gap-2 px-2.5 py-2 text-left text-xs transition-colors',
                  targetId === t.id ? 'bg-accent/60' : 'hover:bg-muted/50'
                )}
              >
                <span className="flex-1 truncate">{t.name}</span>
                {targetId === t.id && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
              </button>
            ))
          )}
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-primary"
            checked={carryParams}
            onChange={(e) => setCarryParams(e.target.checked)}
          />
          Carry over fixed parameter values (they may be specific to this tenant)
        </label>
        {tplLoading && <p className="text-[11px] text-muted-foreground">Loading template…</p>}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add member (existing profile) or invite (new user)
// ---------------------------------------------------------------------------
export function AddMemberDialog({
  tenantId,
  typeOfTenantID,
  open,
  onClose,
}: {
  tenantId: string;
  typeOfTenantID: string;
  open: boolean;
  onClose: () => void;
}) {
  const addMember = useAddMember(tenantId);
  const inviteMember = useInviteMember(tenantId);

  const [mode, setMode] = useState<'existing' | 'invite'>('existing');
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState<ComboboxOption | null>(null);
  const [role, setRole] = useState<string>('USER');
  const [email, setEmail] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');

  const { data: profiles, isLoading: searching } = useProfileSearch(searchText);

  const profileOptions: ComboboxOption[] = (profiles ?? []).map((p) => ({
    label: [p.firstname, p.lastname].filter(Boolean).join(' ') || p.email,
    value: p.id,
  }));

  const pending = addMember.isPending || inviteMember.isPending;

  const reset = () => {
    setSearchText('');
    setSelected(null);
    setRole('USER');
    setEmail('');
    setFirstname('');
    setLastname('');
    setMode('existing');
  };

  const handleAdd = async () => {
    if (!selected) return;
    try {
      await addMember.mutateAsync({ profileID: selected.value, tenantRole: role, typeOfTenantID });
      toast.success(`${selected.label} added`);
      reset();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to add member');
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) return;
    try {
      await inviteMember.mutateAsync({
        email: email.trim(),
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        tenantRole: role,
        typeOfTenantID,
      });
      toast.success(`Invitation sent to ${email.trim()}`);
      reset();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Invitation failed');
    }
  };

  return (
    <Dialog
      open={open}
      title={mode === 'existing' ? 'Add member' : 'Invite new member'}
      onClose={() => {
        if (!pending) {
          reset();
          onClose();
        }
      }}
      busy={pending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          {mode === 'existing' ? (
            <Button size="sm" onClick={handleAdd} disabled={pending || !selected}>
              {pending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Add member
            </Button>
          ) : (
            <Button size="sm" onClick={handleInvite} disabled={pending || !email.trim()}>
              {pending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Send invitation
            </Button>
          )}
        </>
      }
    >
      {mode === 'existing' ? (
        <div className="space-y-3">
          <Field label="Find a profile" hint="Search by name or email (min. 2 characters).">
            <Combobox
              value={selected?.label ?? searchText}
              onChange={(opt) => {
                const match = profileOptions.find((p) => p.value === opt.value);
                if (match) {
                  setSelected(match);
                  setSearchText(match.label);
                } else {
                  setSelected(null);
                  setSearchText(opt.label);
                }
              }}
              options={profileOptions}
              loading={searching}
              placeholder="Start typing a name or email…"
              emptyText="No profiles match"
            />
          </Field>
          {selected && (
            <p className="text-[11px] text-muted-foreground -mt-1">
              {(profiles ?? []).find((p) => p.id === selected.value)?.email}
            </p>
          )}
          <Field label="Role">
            <RoleSelect value={role} onChange={setRole} />
          </Field>
          <button
            type="button"
            onClick={() => setMode('invite')}
            className="text-xs text-primary hover:underline"
          >
            Person not listed? Invite them by email
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <Field label="Email" hint="They'll receive an invitation email to join this tenant.">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <Input value={firstname} onChange={(e) => setFirstname(e.target.value)} />
            </Field>
            <Field label="Last name">
              <Input value={lastname} onChange={(e) => setLastname(e.target.value)} />
            </Field>
          </div>
          <Field label="Role">
            <RoleSelect value={role} onChange={setRole} />
          </Field>
          <button
            type="button"
            onClick={() => setMode('existing')}
            className="text-xs text-primary hover:underline"
          >
            Already has a profile? Search instead
          </button>
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit member role
// ---------------------------------------------------------------------------
export interface RoleEditableMember {
  id: string;
  name: string;
  tenantRole: string | null;
}

export function EditRoleDialog({
  tenantId,
  member,
  open,
  onClose,
}: {
  tenantId: string;
  member: RoleEditableMember | null;
  open: boolean;
  onClose: () => void;
}) {
  const updateRole = useUpdateMemberRole(tenantId);
  const [role, setRole] = useState(member?.tenantRole ?? 'USER');

  const handleSave = async () => {
    if (!member) return;
    try {
      await updateRole.mutateAsync({ id: member.id, tenantRole: role });
      toast.success(`Role updated for ${member.name}`);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Dialog
      open={open}
      title={`Role for ${member?.name ?? ''}`}
      onClose={onClose}
      busy={updateRole.isPending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={updateRole.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updateRole.isPending}>
            {updateRole.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Save role
          </Button>
        </>
      }
    >
      <Field label="Tenant role">
        <RoleSelect value={role} onChange={setRole} />
      </Field>
    </Dialog>
  );
}
